// RECIPETABLE1 (docs/build-briefs/RECIPETABLE1-recipes-table.md; design/style-guide.md sections 4, 5, 9). The pieces of the Settings >
// Recipes drinks table that are plain DOM with no session or list state: the header row, the Requires liquor cell, and the sticky
// selected strip. The table uses the inventory list's own styles (`inv-table`) and the strip is the style guide's bulk strip
// (`fl-selected-strip`), so the two lists look and behave alike. admin.ts owns the rows, the selection and the calls.

import { BULK_RECIPE_COPY, selectAllLabel, selectedLabel, type BulkRecipeField } from "./recipeBulkModel.ts";

export interface RecipeTableColumn {
  key: "select" | "drink" | "category" | "requires" | "active" | "actions";
  label: string;
  width: string;
}

/** Column widths add to 100 so the fixed layout never scrolls sideways. The checkbox column is absent for a person who cannot edit. */
export function recipeTableColumns(selectable: boolean): RecipeTableColumn[] {
  const columns: RecipeTableColumn[] = [
    { key: "select", label: "", width: "4%" },
    { key: "drink", label: "Drink", width: selectable ? "26%" : "30%" },
    { key: "category", label: "Display category", width: "13%" },
    { key: "requires", label: "Requires liquor", width: "19%" },
    { key: "active", label: "Active", width: "14%" },
    { key: "actions", label: "", width: "24%" },
  ];
  return selectable ? columns : columns.filter((column) => column.key !== "select");
}

export type RecipeSelectState = "none" | "some" | "all";

export function createRecipeHead(options: {
  selectable: boolean;
  selectState: RecipeSelectState;
  onToggleAll: (checked: boolean) => void;
}): HTMLElement {
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  for (const column of recipeTableColumns(options.selectable)) {
    const th = document.createElement("th");
    th.scope = "col";
    th.style.width = column.width;
    th.className = `recipe-th recipe-col-${column.key}`;
    if (column.key === "select") {
      const box = document.createElement("input");
      box.type = "checkbox";
      box.className = "recipe-select-box";
      box.dataset["testid"] = "admin-drink-select-all";
      box.setAttribute("aria-label", "Select all shown");
      box.checked = options.selectState === "all";
      box.indeterminate = options.selectState === "some";
      box.addEventListener("change", () => options.onToggleAll(box.checked));
      th.append(box);
    } else if (column.key === "actions") {
      const hidden = document.createElement("span");
      hidden.className = "sr-only";
      hidden.textContent = "Actions";
      th.append(hidden);
    } else {
      th.textContent = column.label;
    }
    headRow.append(th);
  }
  thead.append(headRow);
  return thead;
}

/** The Requires liquor cell: "Off", or the allowed liquor categories as small chips, A to Z by the label shown. */
export function createRequiresLiquorCell(requires: boolean, slotLabels: readonly string[]): HTMLElement {
  const cell = document.createElement("td");
  cell.className = "recipe-col-requires";
  cell.dataset["testid"] = "admin-drink-row-requires-liquor";
  if (!requires) {
    const off = document.createElement("span");
    off.className = "recipe-requires-off";
    off.textContent = BULK_RECIPE_COPY.offWord;
    cell.append(off);
    return cell;
  }
  const sorted = [...slotLabels].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  if (sorted.length === 0) {
    cell.textContent = BULK_RECIPE_COPY.onWord;
    return cell;
  }
  const chips = document.createElement("span");
  chips.className = "recipe-chips";
  for (const label of sorted) {
    const chip = document.createElement("span");
    chip.className = "recipe-chip";
    chip.dataset["testid"] = "admin-drink-liquor-chip";
    chip.textContent = label;
    chips.append(chip);
  }
  cell.append(chips);
  return cell;
}

/** Draws the selected strip into `host`: "N selected", one button per bulk field, Select all N shown, Clear. Hidden while nothing is ticked. */
export function renderRecipeStrip(
  host: HTMLElement,
  options: {
    count: number;
    shownCount: number;
    onField: (field: BulkRecipeField) => void;
    onSelectAll: () => void;
    onClear: () => void;
  },
): void {
  host.replaceChildren();
  host.hidden = options.count === 0;
  if (options.count === 0) return;
  const count = document.createElement("span");
  count.className = "recipe-strip-count";
  count.dataset["testid"] = "admin-drink-bulk-count";
  count.setAttribute("role", "status");
  count.textContent = selectedLabel(options.count);
  host.append(count);
  for (const [field, testId] of [
    ["category", "admin-drink-bulk-category"],
    ["active", "admin-drink-bulk-active"],
    ["requiresLiquor", "admin-drink-bulk-requires-liquor"],
    ["buildCharge", "admin-drink-bulk-build-charge"],
  ] as const) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "fl-btn";
    button.dataset["testid"] = testId;
    button.textContent = BULK_RECIPE_COPY[field];
    button.addEventListener("click", () => options.onField(field));
    host.append(button);
  }
  const spacer = document.createElement("span");
  spacer.className = "recipe-strip-spacer";
  host.append(spacer);
  const selectAll = document.createElement("button");
  selectAll.type = "button";
  selectAll.className = "fl-btn fl-btn--ghost";
  selectAll.dataset["testid"] = "admin-drink-bulk-select-all";
  selectAll.textContent = selectAllLabel(options.shownCount);
  selectAll.disabled = options.count === options.shownCount;
  selectAll.addEventListener("click", () => options.onSelectAll());
  const clear = document.createElement("button");
  clear.type = "button";
  clear.className = "fl-btn fl-btn--ghost";
  clear.dataset["testid"] = "admin-drink-bulk-clear";
  clear.textContent = BULK_RECIPE_COPY.clear;
  clear.addEventListener("click", () => options.onClear());
  host.append(selectAll, clear);
}
